import {
    OpenAPI, SubscriptionService,
    TokenAuthorization,
    User, UserAgentService,
    UserHostService,
    UserLoginService,
    UserOverviewPage,
    UserPreferencesService,
    UserService,
    UserVanliferService,
    Compound
} from '../../services/restserver-openapi';
import {request as __request} from '../../services/restserver-openapi/core/request';
import {
    mappingAgentModelToDto,
    mappingHostModelToDto,
    mappingVanliferModelToDto,
    remappingModel
} from "../../services/dataModels/UserModel";
import {mappingPreferencesModelToDto, PreferencesModel} from "../../services/dataModels/PreferencesModel";

const {getAuthorization} = UserLoginService;
const {getUserSpecificationId} = UserService;
const {createHostUser, updateHost, getHost, deleteHost} = UserHostService;
const {createVanliferUser, getVanlifer, updateVanlifer, deleteVanlifer} = UserVanliferService;
const {createAgentUser, updateAgent, getAgent, deleteAgent} = UserAgentService;
const {updatePreferences, getPreferences} = UserPreferencesService;
const {getUserEntitlements, getUserSubscriptions} = SubscriptionService;


export function setTokenInApi(identityToken: string) {
    OpenAPI.TOKEN = identityToken;
}

const getAuthApiUrl = (path: string) => `${process.env.REACT_APP_API_URL ?? ''}${path}`;

const extractApiErrorMessage = async (response: Response, fallbackMessage: string): Promise<string> => {
    try {
        const rawBody = await response.text();
        if (!rawBody) {
            return fallbackMessage;
        }

        try {
            const parsedBody = JSON.parse(rawBody);
            return parsedBody.message || parsedBody.error || fallbackMessage;
        } catch {
            return rawBody;
        }
    } catch {
        return fallbackMessage;
    }
}

export const getUserToken = async (_token: string) => {
    try {
        const token : TokenAuthorization = await getAuthorization();
        return token;
    } catch (error) {
        throw new Error();
    }
}

export const requestEmailOtpLogin = async (email: string): Promise<void> => {
    const response = await fetch(getAuthApiUrl('/users/login/email-otp/request'), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
    });

    if (!response.ok) {
        throw new Error(await extractApiErrorMessage(response, 'OTP request failed'));
    }
}

export const loginWithEmailOtp = async (email: string, otp: string): Promise<TokenAuthorization> => {
    const response = await fetch(getAuthApiUrl('/users/login/email-otp'), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, otp }),
    });

    if (!response.ok) {
        throw new Error(await extractApiErrorMessage(response, 'Email OTP login failed'));
    }

    return response.json();
}

export const verifyEmailOtp = async (email: string, otp: string): Promise<TokenAuthorization> => {
    const response = await fetch(getAuthApiUrl('/users/login/email-otp/verify'), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, otp }),
    });

    if (!response.ok) {
        throw new Error(await extractApiErrorMessage(response, 'Email OTP verification failed'));
    }

    return response.json();
}


export const createUser = async (inputUser: User, userRole: string | undefined) => {
    try {
        if (userRole === 'HOST') {
            const inputHost = mappingHostModelToDto(inputUser, 0, false);
            const response = await createHostUser(inputHost);
            return remappingModel(response);
        } else if (userRole === 'VANLIFER') {
            const inputVanlifer = mappingVanliferModelToDto(inputUser);
            const response = await createVanliferUser(inputVanlifer);
            return remappingModel(response);
        } else if (userRole === 'AGENT') {
            const inputAgent = mappingAgentModelToDto(inputUser, 0, false);
            const response = await createAgentUser(inputAgent);
            return remappingModel(response);
        }
    } catch (error: any) {
        if (error?.body && error?.status) {
            const message =
                typeof error.body === "string"
                    ? error.body
                    : error.body?.message || error.body?.error || "Unknown error";

            const wrapped = new Error(message);
            (wrapped as any).status = error.status;
            (wrapped as any).body = error.body;
            throw wrapped;
        }

        throw new Error("Unexpected error during user update");
    }
}

export const putUser = async (inputUser: User, specificationUserId: number, userRole: string, verified: boolean = false) => {
    try {
        if (userRole === User.userRole.HOST) {
            const inputHost = mappingHostModelToDto(inputUser, specificationUserId, verified);
            const response = await updateHost(specificationUserId, inputHost);
            return remappingModel(response);
        } else if (userRole === User.userRole.VANLIFER) {
            const inputVanlifer = mappingVanliferModelToDto(inputUser, specificationUserId);
            const response = await updateVanlifer(specificationUserId, inputVanlifer);
            return remappingModel(response);
        } else {
            const inputAgent = mappingAgentModelToDto(inputUser, specificationUserId, verified);
            const response = await updateAgent(specificationUserId, inputAgent);
            return remappingModel(response);
        }
    } catch (error: any) {
        if (error?.body && error?.status) {
            const message =
                typeof error.body === "string"
                    ? error.body
                    : error.body?.message || error.body?.error || "Unknown error";

            const wrapped = new Error(message);
            (wrapped as any).status = error.status;
            (wrapped as any).body = error.body;
            throw wrapped;
        }

        throw new Error("Unexpected error during user update");
    }
}

export const deleteUser = async (specificationUserId: number, userRole: string) => {
    try {
        if (userRole === User.userRole.HOST) {
            return deleteHost(specificationUserId);
        } else if (userRole === User.userRole.VANLIFER) {
            return deleteVanlifer(specificationUserId);
        } else {
            return deleteAgent(specificationUserId);
        }
    } catch (error: any) {
        if (error?.body && error?.status) {
            const message =
                typeof error.body === "string"
                    ? error.body
                    : error.body?.message || error.body?.error || "Unknown error";

            const wrapped = new Error(message);
            (wrapped as any).status = error.status;
            (wrapped as any).body = error.body;
            throw wrapped;
        }

        throw new Error("Unexpected error during user delete");
    }
}

export const toggleUserVerification = async (inputUser: User, specificationId: number, userRole: string, newVerifiedValue: boolean) => {
    try {
        if (userRole === User.userRole.HOST) {
            const inputHost = mappingHostModelToDto(inputUser, specificationId, newVerifiedValue);
            return updateHost(specificationId, inputHost);
        } else if (userRole === User.userRole.AGENT) {
            const inputAgent = mappingAgentModelToDto(inputUser, specificationId, newVerifiedValue);
            return updateAgent(specificationId, inputAgent);
        } else {
            return undefined;
        }
    } catch (error) {
        throw new Error();
    }
}


export const getUser = async (specificationUserId: number, userRole: string) => {
    try {
        if (userRole === User.userRole.HOST) {
            return getHost(specificationUserId);
        } else if (userRole === User.userRole.VANLIFER) {
            return getVanlifer(specificationUserId);
        } else if (userRole === User.userRole.AGENT) {
            return getAgent(specificationUserId);
        } else {
            return undefined;
        }
    } catch (err) {
        throw new Error("Person not found");
    }
}

export const setPreferences = async (userId: number, inputPreferences: PreferencesModel) => {
    const mappedPreferences = mappingPreferencesModelToDto(userId, inputPreferences);
    try {
       return  updatePreferences(userId, mappedPreferences);
    } catch {
        throw new Error("Cannot add Preferences");
    }
}

export const getAsyncPreferences = async (userId: number) => {
    try {
        return getPreferences(userId);
    } catch {
        throw new Error("Preferences not Found");
    }
}

export const getAsyncUserEntitlements = async (userId: number|undefined = undefined) => {
    try {
        return getUserEntitlements(userId);
    } catch {
        throw new Error("Entitlements not Found");
    }
}

export const getAsyncSubscriptions = async (userId: number|undefined = undefined) => {
    try {
        return getUserSubscriptions(userId);
    } catch {
        throw new Error("Subscription not Found");
    }
}

export const getSpecificationId = async (userId: number, userRoleInput: User.userRole) => {
    try {
        return getUserSpecificationId(userId, userRoleInput);
    }   catch(err) {
        throw new Error("SpecificationId not Found");
    }
}

export const getAllUsers = async (
    userRole: User.userRole,
    filter: string,
    pageNumber: number,
    pageSize: number,
    direction: "ASC" | "DESC",
    orderBy: string,
    includeDeleted: boolean = false
): Promise<UserOverviewPage> => {
    try {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/users',
            query: {
                userRole,
                filter,
                pageNumber,
                pageSize,
                direction,
                orderBy,
                includeDeleted,
            },
        });
    } catch {
        throw new Error("Users not Found");
    }
}

export type UserOverviewStats = {
    activeUsers: number;
    deletedUsers: number;
    activeChangedSince: number;
    deletedChangedSince: number;
    netChangeSince: number;
}

export type CommunityProfile = {
    communityName: string;
    profilePictureDataUrl?: string;
    profileBio?: string;
}

export const getCommunityProfile = async (communityName: string): Promise<CommunityProfile> => {
    return __request(OpenAPI, {
        method: 'GET',
        url: '/community/{communityName}',
        path: {
            communityName,
        },
    });
}

export const getUserOverviewStats = async (
    userRole: User.userRole,
    period: 'DAY' | 'WEEK' | 'MONTH' | 'YEAR'
): Promise<UserOverviewStats> => {
    return __request(OpenAPI, {
        method: 'GET',
        url: '/users/stats',
        query: {
            userRole,
            period,
        },
    });
}

export const getFilteredCompounds = async (): Promise<Compound[]> => {
    const response = await fetch("/api/compounds/filter?revisionState=true");
    if (!response.ok) {
        throw new Error("Compounds konnten nicht geladen werden");
    }
    return response.json();
};
